import { useHeaderState } from "@faire/deprecated-retailer-visitor-shared/components/Header/useHeaderState";
import { SDUIOnClickHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { SDUIOnViewHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIOnViewHandler/SDUIOnViewHandler";
import { ComponentLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import useDeleteSearchSuggestionQuery from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/SearchDropdown/deleteSearchSuggestionQuery";
import { SDUITextRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITextRenderer";
import { Close } from "@faire/slate/icons/Close";
import { Search } from "@faire/slate/icons/Search";
import { trackSearchSuggestionDismissActionCategoryClick } from "@faire/web-api--source/events/search/actionCategoryClick/suggestionDismiss";
import { trackSearchSuggestionClick } from "@faire/web-api--source/events/search/click/suggestion";
import { eventKey as trackSearchSuggestionsViewKey } from "@faire/web-api--source/events/search/view/suggestions";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { ILoggingAction } from "@faire/web-api--source/indigofair/data/ILoggingAction";
import { ISDUIImage } from "@faire/web-api--source/indigofair/data/ISDUIImage";
import { ISDUIText } from "@faire/web-api--source/indigofair/data/ISDUIText";
import { ISearchSuggestionPill } from "@faire/web-api--source/indigofair/data/ISearchSuggestionPill";
import { ISearchSuggestionsComponent } from "@faire/web-api--source/indigofair/data/ISearchSuggestionsComponent";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { Spacer } from "@faire/web/slate/spacing";
import { useCallback, useMemo, useState } from "react";

interface SDUISearchSuggestionItemProps {
  onPillClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  image: ISDUIImage | undefined;
  text: ISDUIText | undefined;
  dismiss_request:
    | ISearchSuggestionPill.IDismissSearchSuggestionRequest
    | undefined;
  onPillDelete: () => void;
  loggingData: ComponentLoggingEventParams;
  eventID: string | undefined;
}

export const SDUISearchSuggestionPillRenderer = ({
  pill,
  loggingData,
  onPillDismiss,
  onMouseLeave,
  prefetchUrl,
  variant,
}: {
  pill: ISearchSuggestionPill;
  loggingData: ComponentLoggingEventParams;
  onPillDismiss: () => void;
  onMouseLeave: () => void;
  prefetchUrl: (url: string) => void;
  variant:
    | keyof typeof ISearchSuggestionsComponent.SearchSuggestionVariant
    | undefined;
}) => {
  const [shouldShowPill, setShouldShowPill] = useState(true);
  const { image, text, actions, dismiss_request, event_id } = pill;
  const { hideSearchQuery } = useHeaderState();

  const onPillDelete = () => {
    onPillDismiss();
    setShouldShowPill(false);
  };

  const onPillClick = useCallback(() => {
    hideSearchQuery();
    trackSearchSuggestionClick(
      loggingData.component_name ?? "",
      loggingData.request_id ?? "",
      pill.event_id ?? ""
    );
  }, [hideSearchQuery, loggingData, pill.event_id]);

  const viewAction = useMemo(() => {
    return [
      IAction.build({
        log_event: ILoggingAction.build({
          event_name: trackSearchSuggestionsViewKey,
          parameters: [
            loggingData.component_name ?? "",
            loggingData.request_id ?? "",
            pill.event_id ?? "",
          ],
        }),
      }),
    ];
  }, [loggingData, pill.event_id]);

  const onMouseEnter = useCallback(() => {
    if (actions.length > 0) {
      const url = actions?.[0]?.navigate?.link;
      if (url) {
        prefetchUrl(url);
      }
    }
  }, [actions, prefetchUrl]);

  return (
    <>
      {shouldShowPill ? (
        <SDUIOnViewHandler loggingData={loggingData} onViewActions={viewAction}>
          <SDUIOnClickHandler
            onClickActions={actions}
            loggingData={loggingData}
          >
            <SearchSuggestionItem
              variant={variant}
              onPillClick={onPillClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              image={image}
              text={text}
              dismiss_request={dismiss_request}
              onPillDelete={onPillDelete}
              loggingData={loggingData}
              eventID={event_id}
            />
          </SDUIOnClickHandler>
        </SDUIOnViewHandler>
      ) : null}
    </>
  );
};

const SearchSuggestionItem = ({
  variant,
  ...props
}: SDUISearchSuggestionItemProps & {
  variant:
    | keyof typeof ISearchSuggestionsComponent.SearchSuggestionVariant
    | undefined;
}) => {
  if (variant === ISearchSuggestionsComponent.SearchSuggestionVariant.COLUMN) {
    return <SearchSuggestionFullWidthItem {...props} />;
  }
  return <SearchSuggestionPill {...props} />;
};

const SearchSuggestionPill = ({
  onPillClick,
  onMouseEnter,
  onMouseLeave,
  image,
  text,
  dismiss_request,
  onPillDelete,
  loggingData,
  eventID,
}: SDUISearchSuggestionItemProps) => {
  const imageUrl = image?.image?.url
    ? optimizeImageUrl({ url: image.image.url }, false, {
        height: 32,
        width: 32,
        resizeMethod: "fill",
      })
    : undefined;

  return (
    <div
      className="bg-core-surface-subdued/50 hover:bg-core-surface-subdued m-1 flex h-10 items-center rounded-[20px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--webkit-focus-ring-color)]"
      data-test-id="SDUI-search-suggestion-pill"
      onClick={onPillClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {imageUrl ? (
        <img
          className="m-1 mr-2 ml-1 h-8 w-8 rounded-full object-cover"
          src={imageUrl}
          alt=""
        />
      ) : (
        <Spacer size="2x" />
      )}
      <SDUITextRenderer text={text} />
      <DeleteQueryButton
        dismiss_request={dismiss_request}
        onPillDelete={onPillDelete}
        loggingData={loggingData}
        eventID={eventID}
        fontSize="8px"
      />
    </div>
  );
};

const SearchSuggestionFullWidthItem = ({
  onPillClick,
  onMouseEnter,
  onMouseLeave,
  image,
  text,
  dismiss_request,
  onPillDelete,
  loggingData,
  eventID,
}: SDUISearchSuggestionItemProps) => {
  const imageUrl = image?.image?.url
    ? optimizeImageUrl({ url: image.image.url }, false, {
        height: 40,
        width: 40,
        resizeMethod: "fill",
      })
    : undefined;

  return (
    <div
      className="hover:bg-core-surface-subdued flex h-[48px] cursor-pointer items-center py-[4px] hover:-mx-[16px] hover:px-[16px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--webkit-focus-ring-color)]"
      data-test-id="SDUI-search-suggestion-pill"
      onClick={onPillClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-1 items-center gap-x-[12px]">
        {imageUrl ? (
          <img
            className="flex h-[40px] w-[40px] flex-shrink-0 rounded-full object-cover"
            src={imageUrl}
            alt=""
          />
        ) : (
          <div className="bg-core-surface-subdued flex h-[40px] w-[40px] items-center justify-center rounded-full">
            <Search className="text-core-icon-subdued h-[16px] w-[16px] stroke-[1px]" />
          </div>
        )}
        <SDUITextRenderer text={text} />
      </div>
      <DeleteQueryButton
        dismiss_request={dismiss_request}
        onPillDelete={onPillDelete}
        loggingData={loggingData}
        eventID={eventID}
        fontSize="12px"
      />
    </div>
  );
};

const DeleteQueryButton = ({
  dismiss_request,
  onPillDelete,
  loggingData,
  eventID,
  fontSize,
}: {
  dismiss_request?: ISearchSuggestionPill.IDismissSearchSuggestionRequest;
  onPillDelete: () => void;
  loggingData: ComponentLoggingEventParams;
  eventID?: string;
  fontSize: string;
}) => {
  const { deleteSearchSuggestion } = useDeleteSearchSuggestionQuery();

  const handleDeleteSearchSuggestion = useCallback(
    (event: React.MouseEvent) => {
      onPillDelete();

      // Preventing navigation behavior
      event.stopPropagation();
      event.preventDefault();
      if (dismiss_request) {
        deleteSearchSuggestion([dismiss_request]);
      }

      if (loggingData.request_id && eventID) {
        trackSearchSuggestionDismissActionCategoryClick(
          loggingData.component_name ?? "",
          loggingData.request_id,
          eventID
        );
      }
    },
    [
      deleteSearchSuggestion,
      dismiss_request,
      onPillDelete,
      loggingData,
      eventID,
    ]
  );

  if (!dismiss_request) {
    return <Spacer size="2x" />;
  }

  return (
    <button
      className="text-core-icon-subdued hover:text-core-icon-hover mr-1.5 ml-1.5 flex h-7 items-center justify-center stroke-[2px] pr-2 pl-0.5"
      onClick={handleDeleteSearchSuggestion}
      data-test-id="SDUI-search-suggestion-pill-delete-button"
    >
      <Close fontSize={fontSize} />
    </button>
  );
};
